import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { getCanonicalCartSnapshot } from './tpl-cart-canonical.js';

/**
 * @typedef {object} ProductVariant
 * @property {number} id
 * @property {string} [title]
 * @property {boolean} [available]
 * @property {string | { src?: string }} [featured_image]
 * @property {{ preview_image?: { src?: string } }} [featured_media]
 */

/**
 * @typedef {object} Product
 * @property {number} id
 * @property {string} [title]
 * @property {number} [price]
 * @property {string} [url]
 * @property {ProductVariant[]} [variants]
 * @property {string | { src?: string }} [featured_image]
 */

/**
 * @typedef {object} CartLine
 * @property {number} [product_id]
 * @property {number} [final_line_price]
 */

/**
 * @typedef {object} CartSnapshot
 * @property {number} [item_count]
 * @property {CartLine[]} [items]
 */

/**
 * @typedef {object} CardRenderContext
 * @property {string} sectionId
 * @property {string} moneyFormat
 * @property {string} currencyCode
 * @property {string} cartAddUrl
 * @property {string} addToCartLabel
 * @property {string} chooseOptionsLabel
 * @property {string} addToCartIconMarkup
 */

const RECOMMENDATION_INTENT = 'complementary';
const RECOMMENDATIONS_PER_SEED = 10;
const TOP_CART_SEED_COUNT = 3;
const RECOMMENDATION_SLOT_COUNT = 9;
const CART_UPDATE_DEBOUNCE_MS = 220;

const RootStateClass = Object.freeze({
  HasProducts: 'tpl-promo-banner-root--has-products',
  IsLoading: 'tpl-promo-banner-root--is-loading',
});

const ContainerStateClass = Object.freeze({
  Refreshing: 'tpl-promo-banner__products--refreshing',
});

/**
 * Returns the storefront route prefix Shopify exposes on `window.Shopify`.
 * Falls back to '/' so the upsell still works on a non-Shopify preview.
 *
 * @returns {string}
 */
function getShopifyRouteRoot() {
  const shopify = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify);
  return shopify?.routes?.root || '/';
}

/**
 * @param {string | number | undefined | null} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * `<tpl-promo-cart-upsell>` — cart-aware complementary product strip.
 *
 * The element ships hidden on the storefront. When connected, it picks the
 * top three cart line-items by price (or uses pre-filled `data-cart-source-ids`),
 * fetches complementary recommendations for each via the Ajax Product
 * Recommendations API, and renders up to nine deduped product cards.
 *
 * Lifecycle states are tracked with two BEM modifiers on the host element so
 * CSS can drive the visual transition (collapse, skeleton, shimmer, ready):
 *
 *   - `tpl-promo-banner-root--is-loading`   while a fetch is in flight
 *   - `tpl-promo-banner-root--has-products` once cards are on screen
 *
 * Re-renders are triggered by the theme-wide `cart:update` event and
 * debounced to avoid thrash when several mutations land in the same tick.
 */
class TplPromoCartUpsell extends Component {
  /** @type {AbortController | null} */
  #inFlightRequest = null;

  /**
   * Monotonic counter so in-flight async work from an older render cannot call
   * `#enterReadyState` after the cart has been cleared (e.g. user removes the
   * seed product last while a recommendations fetch is still resolving).
   * AbortController alone is not enough: the microtask can already be past
   * the last `await` when `abort()` runs.
   */
  #renderGeneration = 0;

  #handleCartUpdateDebounced = debounce(
    () => this.#renderRecommendations({ refreshSourceIds: true }),
    CART_UPDATE_DEBOUNCE_MS
  );

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Empty-cart drawer templates keep a hidden host mounted so hydration morph
    // has something to update after the next add-to-cart. Do not fetch on that
    // initial empty mount: during "remove last item" the cart/change request is
    // still pending and `/cart.js` can briefly return the old cart, causing a
    // stale upsell flash.
    if (!this.hasAttribute('data-empty-cart-template')) {
      queueMicrotask(() => this.#renderRecommendations({ refreshSourceIds: true }));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#inFlightRequest?.abort();
  }

  /** @param {CustomEvent<{ resource?: CartSnapshot; data?: { itemCount?: number } }>} event */
  #handleCartUpdate = (event) => {
    // When the cart empties we want the banner gone immediately — before the
    // cart-section morph lands — otherwise stale recommendations flash for the
    // duration of the cart-update round-trip + debounce.
    //
    // `cart-items-component` passes the sections-AJAX payload as `resource` and
    // puts the parsed count in `detail.data.itemCount` (see CartUpdateEvent in
    // component-cart-items.js). Prefer that field — `resource.item_count` is
    // often absent on that payload.
    const detail = event?.detail;
    const itemCount =
      typeof detail?.data?.itemCount === 'number'
        ? detail.data.itemCount
        : typeof detail?.resource?.item_count === 'number'
          ? detail.resource.item_count
          : undefined;

    if (itemCount === 0) {
      this.setAttribute('data-empty-cart-template', '');
      this.#renderGeneration++;
      this.#handleCartUpdateDebounced.cancel();
      this.#inFlightRequest?.abort();
      const productsContainer = this.#getProductsContainer();
      if (productsContainer) this.#enterEmptyState(productsContainer);
      return;
    }

    this.removeAttribute('data-empty-cart-template');
    queueMicrotask(() => this.#handleCartUpdateDebounced());
  };

  /* ------------------------------------------------------------------------
   * Render orchestration
   * --------------------------------------------------------------------- */

  /**
   * @param {{ refreshSourceIds?: boolean }} [options]
   * @returns {Promise<void>}
   */
  async #renderRecommendations(options = {}) {
    const productsContainer = this.#getProductsContainer();
    if (!productsContainer) return;

    const generation = ++this.#renderGeneration;

    this.#inFlightRequest?.abort();
    this.#inFlightRequest = new AbortController();
    const { signal } = this.#inFlightRequest;

    // Only paint a loading state when we already have cards on screen (a cart
    // refresh). On the very first fetch we stay hidden — we don't yet know
    // whether recommendations will come back, so a skeleton would just flash.
    this.#enterLoadingState(productsContainer);

    const sourceProductIds = await this.#getSourceProductIds(
      options.refreshSourceIds === true,
      signal
    );

    if (generation !== this.#renderGeneration) return;

    if (sourceProductIds.length === 0) {
      this.#enterEmptyState(productsContainer);
      return;
    }

    try {
      const recommendationsPerSeed = await Promise.all(
        sourceProductIds.map((productId) => this.#fetchComplementaryProducts(productId, signal))
      );

      if (generation !== this.#renderGeneration) return;

      const uniqueRecommendedProducts = this.#pickRecommendationSlots(recommendationsPerSeed);
      if (uniqueRecommendedProducts.length === 0) {
        if (generation !== this.#renderGeneration) return;
        this.#enterEmptyState(productsContainer);
        return;
      }

      const renderContext = this.#buildCardRenderContext();
      const cardsHtml = uniqueRecommendedProducts
        .map((product, index) => this.#buildProductCardHtml(product, index, renderContext))
        .filter(Boolean)
        .join('');

      if (generation !== this.#renderGeneration) return;

      if (cardsHtml === '') {
        this.#enterEmptyState(productsContainer);
        return;
      }

      if (generation !== this.#renderGeneration) return;

      this.#enterReadyState(productsContainer, cardsHtml);
    } catch (error) {
      if (generation !== this.#renderGeneration) return;
      if (signal.aborted) return;
      console.error('[tpl-promo-cart-upsell] failed to render recommendations', error);
      this.#enterEmptyState(productsContainer);
    }
  }

  /* ------------------------------------------------------------------------
   * State transitions
   *
   * Mirrors how the theme drives `cart-total` updates: real cards stay in
   * place, a shimmer overlay signals the refresh, then new cards crossfade in.
   * --------------------------------------------------------------------- */

  /** @param {HTMLElement} productsContainer */
  #enterLoadingState(productsContainer) {
    const hasRealProductCards = Boolean(productsContainer.querySelector('.tpl-promo-banner__track'));

    this.classList.add(RootStateClass.IsLoading);

    if (hasRealProductCards) {
      // Refresh: keep --has-products so CSS keeps the banner visible (only
      // --has-products gates display — never strip it until we know we're empty).
      this.hidden = false;
      productsContainer.classList.add(ContainerStateClass.Refreshing);
    } else if (!this.#isEditorPreview()) {
      // First-time fetch (no cards yet): collapsed via CSS (--has-products off)
      // + hidden until real cards arrive.
      this.classList.remove(RootStateClass.HasProducts);
      this.hidden = true;
    }

    productsContainer.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {HTMLElement} productsContainer
   * @param {string} cardsHtml
   */
  #enterReadyState(productsContainer, cardsHtml) {
    productsContainer.classList.remove(ContainerStateClass.Refreshing);
    productsContainer.removeAttribute('aria-busy');

    productsContainer.innerHTML =
      `<div class="tpl-promo-banner__track tpl-promo-banner__track--enter">` +
      `<div class="tpl-promo-banner__row">${cardsHtml}</div>` +
      `</div>`;

    this.classList.remove(RootStateClass.IsLoading);
    this.classList.add(RootStateClass.HasProducts);
    this.hidden = false;
  }

  /** @param {HTMLElement} productsContainer */
  #enterEmptyState(productsContainer) {
    productsContainer.classList.remove(ContainerStateClass.Refreshing);
    productsContainer.removeAttribute('aria-busy');
    productsContainer.innerHTML = '';

    this.classList.remove(RootStateClass.IsLoading);
    this.classList.remove(RootStateClass.HasProducts);

    if (!this.#isEditorPreview()) {
      this.hidden = true;
    }
  }

  /* ------------------------------------------------------------------------
   * Data fetching
   * --------------------------------------------------------------------- */

  /**
   * @param {boolean} forceRefresh
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>}
   */
  async #getSourceProductIds(forceRefresh, signal) {
    let sourceIds = this.#readCachedSourceIds();

    if (forceRefresh || sourceIds.length === 0) {
      sourceIds = await this.#fetchTopCartProductIds(signal);
      this.dataset.cartSourceIds = sourceIds.join(',');
    }

    return sourceIds;
  }

  /** @returns {string[]} */
  #readCachedSourceIds() {
    return (this.dataset.cartSourceIds || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>}
   */
  async #fetchTopCartProductIds(signal) {
    let cart;
    try {
      cart = await getCanonicalCartSnapshot();
    } catch {
      return [];
    }
    if (signal.aborted) return [];
    /** @type {CartLine[]} */
    const cartLines = Array.isArray(cart.items) ? [...cart.items] : [];

    cartLines.sort((a, b) => (b.final_line_price || 0) - (a.final_line_price || 0));

    const seenProductIds = new Set();
    /** @type {string[]} */
    const topProductIds = [];

    for (const line of cartLines) {
      const productId = line.product_id;
      if (!productId || seenProductIds.has(productId)) continue;

      seenProductIds.add(productId);
      topProductIds.push(String(productId));

      if (topProductIds.length >= TOP_CART_SEED_COUNT) break;
    }

    return topProductIds;
  }

  /**
   * @param {string} seedProductId
   * @param {AbortSignal} signal
   * @returns {Promise<Product[]>}
   */
  async #fetchComplementaryProducts(seedProductId, signal) {
    const routeRoot = getShopifyRouteRoot();
    const url =
      `${routeRoot}recommendations/products.json` +
      `?product_id=${seedProductId}` +
      `&limit=${RECOMMENDATIONS_PER_SEED}` +
      `&intent=${RECOMMENDATION_INTENT}`;

    const response = await fetch(url, {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return [];

    const data = await response.json();
    return Array.isArray(data.products) ? /** @type {Product[]} */ (data.products) : [];
  }

  /**
   * Picks up to `RECOMMENDATION_SLOT_COUNT` unique products by interleaving
   * the three seed lists in column-major order so each seed contributes
   * fairly to the visible row.
   *
   * @param {Product[][]} recommendationsPerSeed
   * @returns {Product[]}
   */
  #pickRecommendationSlots(recommendationsPerSeed) {
    const [firstSeed = [], secondSeed = [], thirdSeed = []] = recommendationsPerSeed;
    const seenProductIds = new Set();
    /** @type {Product[]} */
    const uniqueProducts = [];

    for (let slotIndex = 0; slotIndex < RECOMMENDATION_SLOT_COUNT; slotIndex += 1) {
      const round = Math.floor(slotIndex / 3);
      const column = slotIndex % 3;
      const sourceList = column === 0 ? firstSeed : column === 1 ? secondSeed : thirdSeed;
      const candidate = sourceList[round];
      if (!candidate || seenProductIds.has(candidate.id)) continue;

      seenProductIds.add(candidate.id);
      uniqueProducts.push(candidate);
    }

    return uniqueProducts;
  }

  /* ------------------------------------------------------------------------
   * HTML builders
   * --------------------------------------------------------------------- */

  /** @returns {HTMLElement | null} */
  #getProductsContainer() {
    const ref = this.refs.productsContainer ?? this.querySelector('[ref="productsContainer"]');
    const element = Array.isArray(ref) ? ref[0] : ref;
    return element instanceof HTMLElement ? element : null;
  }

  /** @returns {boolean} */
  #isEditorPreview() {
    return this.hasAttribute('data-tpl-editor');
  }

  /** @returns {CardRenderContext} */
  #buildCardRenderContext() {
    const addToCartIconTemplate = this.querySelector('template[data-tpl-promo-atc-icon]');
    return {
      sectionId: this.dataset.sectionId || '',
      moneyFormat: this.dataset.moneyFormat || '${{amount}}',
      currencyCode: this.dataset.currency || 'EUR',
      cartAddUrl: this.dataset.cartAddUrl || '/cart/add',
      addToCartLabel: this.dataset.addLabel || 'Add to cart',
      chooseOptionsLabel: this.dataset.chooseLabel || 'Choose options',
      addToCartIconMarkup: addToCartIconTemplate?.innerHTML?.trim() || '',
    };
  }

  /**
   * @param {Product} product
   * @param {number} index
   * @param {CardRenderContext} renderContext
   * @returns {string}
   */
  #buildProductCardHtml(product, index, renderContext) {
    const variant = this.#firstAvailableVariant(product);
    if (!variant) return '';

    const productTitle = product.title || '';
    const variantLabel =
      variant.title && variant.title !== 'Default Title' ? variant.title : "";
    const formattedPrice = formatMoney(
      product.price ?? 0,
      renderContext.moneyFormat,
      renderContext.currencyCode
    );

    const imageSrc = this.#getProductImageSrc(product, variant);
    const mediaMarkup = imageSrc
      ? `<img class="tpl-promo-banner__card-img" src="${escapeHtml(imageSrc)}" ` +
        `alt="${escapeHtml(productTitle)}" width="65" height="65" loading="lazy" />`
      : `<div class="tpl-promo-banner__card-placeholder"></div>`;

    const hasMultipleVariants = (product.variants || []).length > 1;
    const actionMarkup = hasMultipleVariants
      ? this.#buildChooseOptionsLinkHtml(product, renderContext)
      : this.#buildAddToCartFormHtml(product, variant, index, renderContext);

    return (
      `<div class="tpl-promo-banner__card" data-product-id="${product.id}">` +
      `<div class="tpl-promo-banner__card-media">${mediaMarkup}</div>` +
      `<div class="tpl-promo-banner__card-body">` +
      `<div class="tpl-promo-banner__card-copy">` +
      `<p class="tpl-promo-banner__card-title">${escapeHtml(productTitle)}</p>` +
      `<p class="tpl-promo-banner__card-variant">${escapeHtml(variantLabel)}</p>` +
      `</div>` +
      `<div class="tpl-promo-banner__card-actions">` +
      `<span class="tpl-promo-banner__card-price">${formattedPrice}</span>` +
      actionMarkup +
      `</div>` +
      `</div>` +
      `</div>`
    );
  }

  /**
   * @param {Product} product
   * @param {CardRenderContext} renderContext
   * @returns {string}
   */
  #buildChooseOptionsLinkHtml(product, renderContext) {
    const productTitle = product.title || '';
    const productUrl = product.url || '#';

    return (
      `<a href="${escapeHtml(productUrl)}" ` +
      `class="tpl-promo-banner__add-btn tpl-promo-banner__add-btn--link" ` +
      `aria-label="${escapeHtml(renderContext.chooseOptionsLabel)} — ${escapeHtml(productTitle)}">` +
      `<span class="svg-wrapper tpl-promo-banner__add-btn-icon" aria-hidden="true">` +
      renderContext.addToCartIconMarkup +
      `</span>` +
      `</a>`
    );
  }

  /**
   * @param {Product} product
   * @param {ProductVariant} variant
   * @param {number} index
   * @param {CardRenderContext} renderContext
   * @returns {string}
   */
  #buildAddToCartFormHtml(product, variant, index, renderContext) {
    const productTitle = product.title || '';
    const productUrl = product.url || '';
    const isAvailable = variant.available !== false;
    const formId = `TplPromoDynamic-${renderContext.sectionId}-${product.id}-${index}`;

    return (
      `<product-form-component ` +
      `class="tpl-promo-banner__product-form" ` +
      `data-section-id="${escapeHtml(renderContext.sectionId)}" ` +
      `data-product-id="${product.id}" ` +
      `data-product-url="${escapeHtml(productUrl)}" ` +
      `on:submit="/handleSubmit">` +
      `<div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>` +
      `<form action="${escapeHtml(renderContext.cartAddUrl)}" method="post" ` +
      `id="${formId}" class="shopify-product-form" enctype="multipart/form-data" ` +
      `novalidate data-type="add-to-cart-form">` +
      `<input type="hidden" name="id" ref="variantId" value="${variant.id}" />` +
      `<input type="hidden" name="quantity" value="1" />` +
      `<button type="submit" name="add" class="tpl-promo-banner__add-btn" ` +
      `${isAvailable ? '' : 'disabled'} ` +
      `aria-label="${escapeHtml(renderContext.addToCartLabel)} — ${escapeHtml(productTitle)}">` +
      `<span class="svg-wrapper tpl-promo-banner__add-btn-icon" aria-hidden="true">` +
      renderContext.addToCartIconMarkup +
      `</span>` +
      `</button>` +
      `</form>` +
      `</product-form-component>`
    );
  }

  /**
   * @param {Product} product
   * @returns {ProductVariant | null}
   */
  #firstAvailableVariant(product) {
    const variants = product.variants || [];
    const availableVariant = variants.find((variant) => variant.available !== false);
    return availableVariant || variants[0] || null;
  }

  /**
   * @param {Product} product
   * @param {ProductVariant} variant
   * @returns {string}
   */
  #getProductImageSrc(product, variant) {
    if (typeof variant.featured_image === 'string') return variant.featured_image;
    if (variant.featured_image?.src) return variant.featured_image.src;
    if (variant.featured_media?.preview_image?.src) {
      return variant.featured_media.preview_image.src;
    }
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    return '';
  }
}

if (!customElements.get('tpl-promo-cart-upsell')) {
  customElements.define('tpl-promo-cart-upsell', TplPromoCartUpsell);
}
